import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_3DTransforms() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎬 Advanced CSS: 3D Transforms</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS 3D Transforms let you rotate, translate, and scale elements in 3D
        space. With <code>perspective</code>, elements gain depth and appear
        closer or farther from the viewer. This unlocks effects like flipping
        cards, 3D cubes, and immersive UIs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>rotateX</code>, <code>rotateY</code>, and{" "}
            <code>rotateZ</code> for 3D rotations.
          </li>
          <li>
            <code>translateZ()</code> moves elements closer or farther from the
            screen.
          </li>
          <li>
            <code>perspective</code> defines depth; smaller values create
            stronger 3D effects.
          </li>
          <li>
            <code>transform-style: preserve-3d</code> allows child elements to
            retain 3D positioning.
          </li>
          <li>
            Combine <b>transitions</b> and <b>animations</b> for smooth 3D
            interactions.
          </li>
        </ul>
      </div>

      {/* RotateX */}
      <div className="tutorial-section">
        <h2 className="section-title">🔄 RotateX</h2>
        <CodeCopyBlock code={`div { transform: rotateX(60deg); }`} />
        <div
          style={{
            width: "150px",
            height: "150px",
            background: "#3b82f6",
            color: "#fff",
            margin: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            transform: "rotateX(60deg)",
            transformStyle: "preserve-3d",
          }}
        >
          rotateX
        </div>
      </div>

      {/* RotateY */}
      <div className="tutorial-section">
        <h2 className="section-title">🔄 RotateY</h2>
        <CodeCopyBlock code={`div { transform: rotateY(60deg); }`} />
        <div
          style={{
            width: "150px",
            height: "150px",
            background: "#10b981",
            color: "#fff",
            margin: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            transform: "rotateY(60deg)",
            transformStyle: "preserve-3d",
          }}
        >
          rotateY
        </div>
      </div>

      {/* RotateZ */}
      <div className="tutorial-section">
        <h2 className="section-title">🔄 RotateZ</h2>
        <CodeCopyBlock code={`div { transform: rotateZ(45deg); }`} />
        <div
          style={{
            width: "150px",
            height: "150px",
            background: "#f59e0b",
            color: "#fff",
            margin: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            transform: "rotateZ(45deg)",
            transformStyle: "preserve-3d",
          }}
        >
          rotateZ
        </div>
      </div>

      {/* TranslateZ */}
      <div className="tutorial-section">
        <h2 className="section-title">📍 TranslateZ</h2>
        <CodeCopyBlock code={`div { transform: translateZ(80px); }`} />
        <div
          style={{
            width: "150px",
            height: "150px",
            background: "#ef4444",
            color: "#fff",
            margin: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            transform: "perspective(300px) translateZ(80px)",
          }}
        >
          translateZ
        </div>
      </div>

      {/* Perspective */}
      <div className="tutorial-section">
        <h2 className="section-title">👓 Perspective</h2>
        <CodeCopyBlock code={`div { perspective: 400px; }`} />
        <div
          style={{
            perspective: "400px",
            margin: "1rem",
          }}
        >
          <div
            style={{
              width: "150px",
              height: "150px",
              background: "#8b5cf6",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: "rotateY(60deg)",
              borderRadius: "8px",
            }}
          >
            Perspective
          </div>
        </div>
      </div>

      {/* 3D Cube Demo */}
      <div className="tutorial-section">
        <h2 className="section-title">🧊 3D Cube Demo</h2>
        <p>
          A simple cube built with <code>preserve-3d</code> and rotations.
        </p>
        <CodeCopyBlock
          code={`.cube {
  transform-style: preserve-3d;
  animation: spin 6s infinite linear;
}
.cube div {
  position: absolute;
  width: 100px; height: 100px;
  background: rgba(59,130,246,0.8);
}
`}
        />
        <div
          style={{
            perspective: "600px",
            width: "120px",
            height: "120px",
            margin: "2rem auto",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100px",
              height: "100px",
              transformStyle: "preserve-3d",
              animation: "spin 6s infinite linear",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: "100px",
                height: "100px",
                background: "rgba(59,130,246,0.8)",
                transform: "translateZ(50px)",
              }}
            />
            <div
              style={{
                position: "absolute",
                width: "100px",
                height: "100px",
                background: "rgba(16,185,129,0.8)",
                transform: "rotateY(90deg) translateZ(50px)",
              }}
            />
            <div
              style={{
                position: "absolute",
                width: "100px",
                height: "100px",
                background: "rgba(245,158,11,0.8)",
                transform: "rotateY(-90deg) translateZ(50px)",
              }}
            />
            <div
              style={{
                position: "absolute",
                width: "100px",
                height: "100px",
                background: "rgba(239,68,68,0.8)",
                transform: "rotateX(90deg) translateZ(50px)",
              }}
            />
            <div
              style={{
                position: "absolute",
                width: "100px",
                height: "100px",
                background: "rgba(139,92,246,0.8)",
                transform: "rotateX(-90deg) translateZ(50px)",
              }}
            />
            <div
              style={{
                position: "absolute",
                width: "100px",
                height: "100px",
                background: "rgba(34,197,94,0.8)",
                transform: "rotateY(180deg) translateZ(50px)",
              }}
            />
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Build <b>3D card flip</b> effects using rotateY and perspective.
          </li>
          <li>
            Create interactive <b>3D buttons</b> with hover transforms.
          </li>
          <li>
            Experiment with <b>3D cubes, carousels, and galleries</b>.
          </li>
          <li>
            Combine with <code>animations</code> for smooth 3D rotations.
          </li>
          <li>
            Learn how <b>WebGL</b> and <b>Three.js</b> extend beyond CSS 3D.
          </li>
        </ul>
      </div>
    </div>
  );
}
